// R1b-α — Shared administrative app-shell wrapper.
// Composes the design-system dark-lux AppShell for every `/owner` + `/admin/*` route.
//
// R1b-α Step 1 (Slice A) — top-bar completeness:
//   • Wired GlobalSearch (debounced /api/search, Cmd/Ctrl+K, Esc)
//   • Owner identity block ({email · role_label})
//   • Tenant context chip (live tenant_name via /api/tenants/me)
//   • Notifications bell (badge = /api/notifications/outbox/count)
//   • Approvals quicklink (badge = /api/shell/counters.pending_approvals)
// All new controls have unique data-testids; no existing testids removed.
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import {
  Home, Users, DollarSign, Hammer, Plug, Shield,
  RotateCcw, LifeBuoy, LogOut, ArrowLeft, ChevronRight,
  Handshake, BarChart3, GraduationCap, KeyRound,
  Bell, CheckSquare, Settings as SettingsIcon, ShieldQuestion,
  CreditCard, Images, DraftingCompass, HardHat, Receipt, Share2, Building, Radar,
  FolderKanban,
  Lock,
} from "lucide-react";
import { usePortfolioFeature } from "../../lib/portfolio";
import { ARCHITECT_ROLES, FIELD_ROLES, FINANCE_ROLES, commsNavEntries, moduleNavEntries, useModuleActive, usePhaseFeature } from "../../lib/phaseFeatures";
import { AppShell } from "../shell/AppShell";
import TrainingModeBanner from "../training/TrainingModeBanner";
import { TopBar } from "../shell/TopBar";
import { PortalSwitcherLauncher } from "../nav/PortalSwitcherLauncher";
import ErfBackLogo from "../navigation/ErfBackLogo";
import { useAuth } from "../../lib/auth";
import { api } from "../../lib/api";
import GlobalSearch, { SEARCH_ENABLED } from "../../components/GlobalSearch";
import { homeRouteFor } from "../../lib/roleHome";
import { toast } from "sonner";
import "../index";

// Destinations every signed-in role can open (ProtectedRoute pages), shown to non-admin roles.
const NON_ADMIN_NAV = new Set(["command", "crm", "reports", "help"]);
const NAV_GROUPS_ALL = [
  { key: "command",       label: "Command",           icon: Home,       to: "/owner",              phase: "R1b-α", adminOk: true },
  { key: "people-access", label: "People & Access",   icon: Users,      to: "/admin/people-roles", phase: "R1b-β", adminOk: true },
  { key: "crm",           label: "CRM",               icon: Handshake,  to: "/crm",                phase: "R1c",   adminOk: true },
  // B2a (Finding 002): ONE Financials item. Phase 5 ON + finance role -> the real Phase 5 workspace (resolved below);
  // otherwise the truthful overview (/admin/financial) that says "Available to Activate".
  { key: "financial",     label: "Financials",        icon: DollarSign, to: "/admin/financial",    phase: "R1b-γ", adminOk: false },
  { key: "operations",    label: "Operations",        icon: Hammer,     to: "/admin/operations",   phase: "R1b-γ", adminOk: true },
  { key: "reports",       label: "Reports",           icon: BarChart3,  to: "/reports",            phase: "R1c",   adminOk: true },
  { key: "integrations",  label: "Integrations & API",icon: Plug,       to: "/admin/integrations", phase: "R1b-δ", adminOk: true },
  { key: "comms-settings", label: "Communications Settings", icon: SettingsIcon, to: "/admin/settings/communications", phase: "Stage-A", adminOk: true },
  { key: "audit-trust",   label: "Audit & Trust",     icon: Shield,     to: "/admin/audit-trust",  phase: "R1b-δ", adminOk: true },
  { key: "help",          label: "Help & Learn",      icon: GraduationCap, to: "/learn",           phase: "R1c",   adminOk: true },
];

const MY_PROJECT_ROLES = ["PROJECT_MANAGER", "SUPERINTENDENT", "FIELD_SUPERVISOR", "EMPLOYEE", "ARCHITECT",
  "SUBCONTRACTOR", "VENDOR_SUPPLIER", "CONTRACTOR_1099"];

const ROLE_LABELS = {
  OWNER_SUPER_ADMIN: "Founder Owner",
  PLATFORM_OPERATOR: "Platform Operator",
  ADMINISTRATOR: "Administrator",
  PROJECT_MANAGER: "Project Manager",
  SUPERINTENDENT: "Superintendent",
  ESTIMATOR: "Estimator",
  CLIENT: "Client",
};

const FORM_TAGS = new Set(["INPUT", "TEXTAREA", "SELECT"]);
function isEditableFocus(target) {
  if (!target) return false;
  if (FORM_TAGS.has(target.tagName)) return true;
  if (target.isContentEditable) return true;
  return false;
}

function UserMenu({ isOwner, onRestartTour, onOpenHelp, onLogout, identity, onOpenExplain }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);
  return (
    <div ref={ref} style={{ position: "relative" }}>
      {identity && (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          data-testid="admin-shell-identity"
          aria-haspopup="menu"
          aria-expanded={open}
          title={`${identity.email} · ${identity.roleLabel}`}
          style={{
            background: "transparent",
            border: "1px solid var(--color-border)",
            borderRadius: 999,
            padding: "6px 12px",
            display: "inline-flex", alignItems: "center", gap: 8,
            color: "var(--color-text)",
            fontFamily: "var(--font-body)",
            fontSize: 12,
            cursor: "pointer",
            maxWidth: 260,
            minHeight: 44,
            minWidth: 44,
          }}
        >
          <span
            aria-hidden="true"
            style={{
              width: 22, height: 22, borderRadius: "50%",
              background: "var(--color-primary, #B8892B)",
              color: "#0F0F10", fontFamily: "var(--font-mono)", fontSize: 10, fontWeight: 700,
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0,
            }}
          >
            {(identity.email || "?").slice(0, 1).toUpperCase()}
          </span>
          <span className="ds-shell-identity-text" style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", overflow: "hidden" }}>
            <span
              data-testid="admin-shell-identity-email"
              style={{ maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", lineHeight: 1.1 }}
            >{identity.email}</span>
            <span
              data-testid="admin-shell-identity-role"
              style={{ fontFamily: "var(--font-mono)", fontSize: 9, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--color-text-muted)", lineHeight: 1.2 }}
            >{identity.roleLabel}</span>
          </span>
        </button>
      )}
      {!identity && (
        <button
          type="button"
          className="ds-shell-icon"
          aria-label="User menu"
          aria-haspopup="menu"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          data-testid="admin-shell-user-menu-toggle"
        >
          <LifeBuoy size={16} />
        </button>
      )}
      {open && (
        <div
          role="menu"
          data-testid="admin-shell-user-menu"
          style={{
            position: "absolute", top: "calc(100% + 8px)", right: 0,
            minWidth: 260, background: "var(--color-surface)",
            border: "1px solid var(--color-border)", borderRadius: 10,
            boxShadow: "0 12px 40px rgba(0,0,0,0.45)", padding: 6, zIndex: 55,
          }}
        >
          {identity && (
            <div
              data-testid="admin-shell-user-menu-identity"
              style={{
                padding: "10px 12px 8px",
                borderBottom: "1px solid var(--color-border)",
                marginBottom: 4,
              }}
            >
              <div style={{
                fontFamily: "var(--font-body)", fontSize: 12, color: "var(--color-text)",
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              }}>{identity.email}</div>
              <div
                data-testid="admin-shell-user-menu-role"
                style={{
                  fontFamily: "var(--font-mono)", fontSize: 9, letterSpacing: "0.14em",
                  textTransform: "uppercase", color: "var(--color-primary, #B8892B)",
                  marginTop: 2,
                }}
              >Role · {identity.roleLabel}</div>
            </div>
          )}
          {isOwner && (
            <button
              role="menuitem" type="button"
              onClick={() => { setOpen(false); onRestartTour?.(); }}
              data-testid="restart-owner-tour"
              className="admin-shell-menu-item"
              style={{ color: "var(--color-text)" }}
            >
              <RotateCcw size={14} />
              <span style={{ flex: 1 }}>Restart Owner Tour</span>
            </button>
          )}
          <button
            role="menuitem" type="button"
            onClick={() => { setOpen(false); onOpenHelp?.(); }}
            data-testid="admin-shell-open-help"
            className="admin-shell-menu-item"
            style={{ color: "var(--color-text)" }}
          >
            <LifeBuoy size={14} />
            <span style={{ flex: 1 }}>Help Center</span>
          </button>
          <button
            role="menuitem" type="button"
            onClick={() => { setOpen(false); navigate("/account/password"); }}
            data-testid="admin-shell-change-password"
            className="admin-shell-menu-item"
            style={{ color: "var(--color-text)" }}
          >
            <KeyRound size={14} />
            <span style={{ flex: 1 }}>Change Password</span>
          </button>
          {(identity?.roleLabel === "Founder Owner" || identity?.roleLabel === "Platform Operator") && (
            <button
              role="menuitem" type="button"
              onClick={() => { setOpen(false); navigate("/admin/billing"); }}
              data-testid="admin-shell-billing-link"
              className="admin-shell-menu-item"
              style={{ color: "var(--color-text)" }}
            >
              <CreditCard size={14} />
              <span style={{ flex: 1 }}>Platform Billing</span>
            </button>
          )}
          <button
            role="menuitem" type="button"
            onClick={() => { setOpen(false); navigate("/admin/settings"); }}
            data-testid="admin-shell-settings-link"
            className="admin-shell-menu-item"
            style={{ color: "var(--color-text)" }}
          >
            <SettingsIcon size={14} />
            <span style={{ flex: 1 }}>Settings</span>
          </button>
          {(identity?.roleLabel === "Founder Owner" || identity?.roleLabel === "Platform Operator") && (
            <button
              role="menuitem" type="button"
              onClick={() => { setOpen(false); onOpenExplain?.(); }}
              data-testid="admin-shell-authz-explain"
              className="admin-shell-menu-item"
              style={{ color: "var(--color-text)" }}
            >
              <ShieldQuestion size={14} />
              <span style={{ flex: 1 }}>Explain my permissions</span>
            </button>
          )}
          <div style={{ height: 1, background: "var(--color-border)", margin: "6px 8px" }} />
          <button
            role="menuitem" type="button"
            onClick={() => { setOpen(false); onLogout?.(); }}
            data-testid="admin-shell-logout"
            className="admin-shell-menu-item"
            style={{ color: "var(--color-status-danger)" }}
          >
            <LogOut size={14} />
            <span style={{ flex: 1 }}>Sign out</span>
          </button>
        </div>
      )}
    </div>
  );
}

function TenantChip({ tenantName, tenantId }) {
  if (!tenantName && !tenantId) return null;
  const label = tenantName || tenantId;
  return (
    <span
      data-testid="admin-shell-tenant-chip"
      title={`Tenant: ${label}${tenantId && tenantName ? ` (${tenantId.slice(0, 8)}…)` : ""}`}
      style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        border: "1px solid #B08D57",
        color: "rgba(245, 241, 232, 0.85)",
        borderRadius: 3,
        padding: "3px 10px",
        fontFamily: "var(--font-mono)",
        fontSize: 10,
        letterSpacing: "0.14em",
        textTransform: "uppercase",
        whiteSpace: "nowrap",
        maxWidth: 220,
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
    >
      <span aria-hidden="true" style={{ width: 5, height: 5, borderRadius: "50%", background: "#B08D57" }} />
      {label}
    </span>
  );
}

export function AdminAppShell({
  activeNav = "command",
  title,
  subtitle,
  breadcrumbs,
  children,
  user,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [tenantName, setTenantName] = useState(null);
  const [counters, setCounters] = useState({ pending_outbox: 0, pending_approvals: 0 });
  const [explainOpen, setExplainOpen] = useState(false);
  const isOwner = user?.role_key === "OWNER_SUPER_ADMIN";
  const isRootScreen = location.pathname === "/owner";
  const entryHistoryLenRef = useRef(
    typeof window !== "undefined" ? window.history.length : 0
  );

  useEffect(() => { setMobileNavOpen(false); }, [location.pathname]);

  // Tenant name — fetch once from /api/tenants/me. Failure → keep null, chip
  // falls back to raw tenant_id from the JWT (never fabricated).
  useEffect(() => {
    if (!user?.tenant_id) return;
    let cancelled = false;
    api.get("/tenants/me").then((r) => {
      if (cancelled) return;
      setTenantName(r.data?.legal_name || r.data?.display_name || r.data?.name || null);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [user?.tenant_id]);

  // Shell counters — bell + approvals badges. Refetched on route change so
  // "Recent Outbox" updates land almost immediately. Failures leave the
  // counters at 0 (no fake numbers).
  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    api.get("/shell/counters").then((r) => {
      if (cancelled) return;
      setCounters({
        pending_outbox: r.data?.pending_outbox ?? 0,
        pending_approvals: r.data?.pending_approvals ?? 0,
      });
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [user?.id, location.pathname]);

  const goBack = useCallback(() => {
    if (typeof window !== "undefined" &&
        window.history.length > entryHistoryLenRef.current) {
      navigate(-1);
      return;
    }
    navigate(homeRouteFor(user));
  }, [navigate, user]);

  useEffect(() => {
    if (isRootScreen) return;
    const onKey = (e) => {
      if (isEditableFocus(e.target)) return;
      if (e.altKey && e.key === "ArrowLeft") {
        e.preventDefault();
        goBack();
        return;
      }
      if (e.key === "Backspace" && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault();
        goBack();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isRootScreen, goBack]);

  const portfolioFeature = usePortfolioFeature(user?.id);
  const phase3 = usePhaseFeature(3, user?.id);
  const phase4 = usePhaseFeature(4, user?.id);
  const phase5 = usePhaseFeature(5, user?.id);
  const phase6 = usePhaseFeature(6, user?.id);
  const phase7 = usePhaseFeature(7, user?.id);
  const phase8 = usePhaseFeature(8, user?.id);
  const commsOn = useModuleActive("communications", user?.id);
  // B2a: roles other than Owner/Administrator only see destinations they can open (no redirect-on-click); their
  // "Command" entry is their own home.
  const isAdminRole = isOwner || user?.role_key === "ADMINISTRATOR";
  const navGroups = NAV_GROUPS_ALL
    .filter((g) => isOwner || (g.adminOk && (isAdminRole || NON_ADMIN_NAV.has(g.key))))
    .map((g) => (g.key === "command" && !isOwner ? { ...g, label: "Home", to: homeRouteFor(user) } : g));
  // B2b-2: assigned roles get "My Projects" (assignment-scoped list; the backend filters).
  if (MY_PROJECT_ROLES.includes(user?.role_key)) {
    navGroups.splice(1, 0, { key: "my-projects", label: "My Projects", icon: FolderKanban, to: "/my/projects" });
  }
  // MFA: every signed-in user manages their own sign-in security.
  navGroups.push({ key: "security", label: "Sign-in security", icon: Lock, to: "/settings/security" });
  if (portfolioFeature?.nav_visible) {
    const at = navGroups.findIndex((g) => g.key === "operations") + 1;
    navGroups.splice(at, 0, { key: "portfolio", label: "Portfolio Manager", icon: Images, to: "/admin/portfolio" });
  }
  if (phase3?.enabled && ARCHITECT_ROLES.includes(user?.role_key)) {
    const at = navGroups.findIndex((g) => g.key === "operations") + 1;
    navGroups.splice(at, 0, { key: "architect", label: "Architect Workspace", icon: DraftingCompass, to: "/phase3/architect" },
      { key: "architect-portal", label: "Architect Portal", icon: DraftingCompass, to: "/phase3/architect/portal" });
  }
  if (phase4?.enabled && FIELD_ROLES.includes(user?.role_key)) {
    const at = navGroups.findIndex((g) => g.key === "operations") + 1;
    navGroups.splice(at, 0, { key: "field", label: "Field Operations", icon: HardHat, to: "/phase4/field" });
  }
  if (phase5?.enabled && FINANCE_ROLES.includes(user?.role_key)) {
    const fin = navGroups.findIndex((g) => g.key === "financial");
    const real = { key: "finance", label: "Financials", icon: Receipt, to: "/phase5/billing" };
    if (fin >= 0) navGroups.splice(fin, 1, real);
    else navGroups.splice(navGroups.findIndex((g) => g.key === "operations") + 1, 0, real);
  }
  if (phase6?.enabled && ["OWNER_SUPER_ADMIN", "ADMINISTRATOR"].includes(user?.role_key)) {
    const at = navGroups.findIndex((g) => g.key === "operations") + 1;
    navGroups.splice(at, 0, { key: "clientsharing", label: "Client Sharing", icon: Share2, to: "/phase6/sharing" });
  }
  if (phase7?.enabled && ["OWNER_SUPER_ADMIN", "ADMINISTRATOR", "PROPERTY_MANAGER"].includes(user?.role_key)) {
    const at = navGroups.findIndex((g) => g.key === "operations") + 1;
    navGroups.splice(at, 0, { key: "property", label: "Property Management", icon: Building, to: "/phase7/properties" });
  }
  if (phase8?.enabled && ["OWNER_SUPER_ADMIN", "ADMINISTRATOR"].includes(user?.role_key)) {
    const at = navGroups.findIndex((g) => g.key === "operations") + 1;
    navGroups.splice(at, 0, { key: "iq", label: "Construction IQ", icon: Radar, to: "/phase8/iq" });
  }
  // B12: Finance operations (B10) + Workforce (B11), role-aware and module-aware. Module OFF -> Founder/Admin see
  // "Available to Activate" (opens the module status page); every other role: hidden.
  const opsAt = navGroups.findIndex((g) => g.key === "operations") + 1;
  navGroups.splice(opsAt, 0, ...moduleNavEntries({ phase4, phase5, role: user?.role_key, isAdminRole })
    .map((g) => ({ ...g, icon: g.key === "finance-ops" ? Receipt : HardHat })));
  // modswitch: Notifications center + Delivery recovery belong to the Communications module (B12).
  navGroups.push(...commsNavEntries({ comms: commsOn === null ? null : { enabled: commsOn }, isAdminRole })
    .map((g) => ({ ...g, icon: g.key === "delivery-recovery" ? Plug : Bell })));
  const navItems = navGroups
    .map((g) => {
    const Icon = g.icon;
    const isActive = activeNav === g.key || location.pathname === g.to;
    return {
      key: g.key,
      label: g.label,
      badge: g.badge,
      icon: <Icon size={14} aria-hidden="true" />,
      active: isActive,
      onClick: () => {
        setMobileNavOpen(false);
        navigate(g.to);
      },
    };
  });

  const handleRestartTour = () => {
    if (location.pathname === "/owner") {
      navigate("/owner?tour=restart", { replace: true });
    } else {
      navigate("/owner?tour=restart");
    }
  };

  // Pass 2 — generic chevron Back replaced with the shared ERF-logo back
  // navigator (`<ErfBackLogo/>`). Phase 2–8 surfaces hosted inside this
  // shell inherit the new control automatically. Alt+← + Backspace global
  // keyboard shortcuts above remain active and continue to call `goBack`.
  const backButton = !isRootScreen ? (
    <ErfBackLogo
      fallbackPath={homeRouteFor(user)}
      ariaLabel="Return to previous page"
      tone="dark"
      testId="admin-shell-back-button"
    />
  ) : null;

  const identity = user ? {
    email: user.email || "—",
    roleLabel: ROLE_LABELS[user.role_key] || user.role_key || "User",
  } : null;

  const iconLaunchers = [
    {
      key: "notifications",
      icon: <Bell size={16} aria-hidden="true" />,
      count: counters.pending_outbox,
      ariaLabel: `Notifications outbox (${counters.pending_outbox} in outbox)`,
      title: counters.pending_outbox > 0
        ? `Notifications outbox — ${counters.pending_outbox} in outbox`
        : "Notifications outbox",
      onClick: () => navigate("/admin/notifications/outbox"),
      testId: "admin-shell-notifications",
      badgeTestId: "admin-shell-notifications-badge",
    },
    {
      key: "approvals",
      icon: <CheckSquare size={16} aria-hidden="true" />,
      count: counters.pending_approvals,
      ariaLabel: `Pending approvals (${counters.pending_approvals})`,
      title: counters.pending_approvals > 0
        ? `Pending approvals — ${counters.pending_approvals} awaiting decision`
        : "Approvals inbox",
      onClick: () => navigate("/admin/approvals"),
      testId: "admin-shell-approvals-quicklink",
      badgeTestId: "admin-shell-approvals-badge",
    },
  ];

  const topBar = (
    <TopBar
      searchPlaceholder="Search projects, ledger, users…"
      roleContext={isOwner ? "OWNER" : (user?.role_key || "")}
      search={SEARCH_ENABLED ? <GlobalSearch /> : null}
      trailing={<TenantChip tenantName={tenantName} tenantId={user?.tenant_id} />}
      iconLaunchers={iconLaunchers}
      leading={backButton}
      quickCreate={
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <PortalSwitcherLauncher
            isOwner={isOwner}
            onImpersonate={async (roleKey) => {
              try {
                await auth.startImpersonation(`role:${roleKey}`);
                toast.success(`Impersonating as ${roleKey.replaceAll("_", " ")}`);
                navigate(roleKey === "CLIENT" ? "/client/portal" : "/owner");
              } catch (e) {
                toast.error("Impersonation failed — opening picker instead.");
                navigate("/admin/portal-switch");
              }
            }}
            onOpenPortalSwitch={() => navigate("/admin/portal-switch")}
          />
          <UserMenu
            isOwner={isOwner}
            identity={identity}
            onRestartTour={handleRestartTour}
            onOpenHelp={() => navigate("/help")}
            onOpenExplain={() => setExplainOpen(true)}
            onLogout={() => auth?.logout?.().then(() => navigate("/login"))}
          />
        </div>
      }
    />
  );

  return (
    <AppShell
      theme="erf-operator"
      navItems={navItems}
      topBar={topBar}
      mobileNavOpen={mobileNavOpen}
      onToggleMobileNav={() => setMobileNavOpen((v) => !v)}
    >
      <a
        href="#admin-shell-main"
        className="ds-shell-skip-link"
        data-testid="admin-shell-skip-to-content"
      >
        Skip to main content
      </a>
      <TrainingModeBanner />

      {Array.isArray(breadcrumbs) && breadcrumbs.length > 0 && (
        <nav
          className="ds-shell-breadcrumbs"
          aria-label="Breadcrumb"
          data-testid="admin-shell-breadcrumbs"
        >
          {breadcrumbs.map((crumb, i) => {
            const isLast = i === breadcrumbs.length - 1;
            return (
              <React.Fragment key={`${crumb.label}-${i}`}>
                {isLast || !crumb.to ? (
                  <span
                    className="ds-shell-breadcrumb current"
                    aria-current={isLast ? "page" : undefined}
                    data-testid={`admin-shell-breadcrumb-${i}`}
                  >{crumb.label}</span>
                ) : (
                  <Link
                    to={crumb.to}
                    className="ds-shell-breadcrumb link"
                    data-testid={`admin-shell-breadcrumb-${i}`}
                  >{crumb.label}</Link>
                )}
                {!isLast && (
                  <ChevronRight size={12} className="ds-shell-breadcrumb-sep" aria-hidden="true" />
                )}
              </React.Fragment>
            );
          })}
        </nav>
      )}

      {(title || subtitle) && (
        <div style={{ marginBottom: 24 }}>
          {title && (
            <h1 style={{
              fontFamily: "var(--font-display)",
              fontSize: 28, fontWeight: 600, letterSpacing: "-0.01em",
              color: "var(--color-text)", margin: 0,
            }} data-testid="admin-shell-title">{title}</h1>
          )}
          {subtitle && (
            <div style={{
              fontFamily: "var(--font-mono)", fontSize: 11,
              letterSpacing: "0.14em", textTransform: "uppercase",
              color: "var(--color-text-muted)", marginTop: 6,
            }} data-testid="admin-shell-subtitle">{subtitle}</div>
          )}
        </div>
      )}
      <main
        id="admin-shell-main"
        role="main"
        tabIndex={-1}
        aria-label={title || "Admin content"}
        style={{ background: "var(--color-accent, #F5F1E8)", color: "#0F0F10", padding: 24, borderRadius: 10, borderTop: "1px solid #3B82C4" }}
        data-testid="admin-shell-content"
      >
        {children}
      </main>
      {explainOpen && (
        <AuthzExplainModal onClose={() => setExplainOpen(false)} />
      )}
    </AppShell>
  );
}

function AuthzExplainModal({ onClose }) {
  const [resource, setResource] = React.useState("projects");
  const [action, setAction] = React.useState("edit");
  const [result, setResult] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState("");

  const run = async () => {
    setBusy(true); setErr(""); setResult(null);
    try {
      const r = await api.get(`/authz/explain`, { params: { resource, action } });
      setResult(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail?.message || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="authz-explain-title"
      data-testid="admin-shell-authz-explain-modal"
      style={{
        position: "fixed", inset: 0, zIndex: 90,
        background: "rgba(15,15,16,0.55)",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: 540, width: "92%",
          background: "#1A1A1C", color: "#FAFAF7",
          border: "1px solid #3A3A3D", borderRadius: 12,
          padding: 26, fontFamily: '"Inter Tight", system-ui, sans-serif',
          boxShadow: "0 20px 70px rgba(0,0,0,0.5)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
          <h3 id="authz-explain-title" style={{ fontFamily: '"Fraunces", Georgia, serif', fontSize: 20, margin: 0 }}>
            Explain my permissions
          </h3>
          <button type="button" onClick={onClose} data-testid="admin-shell-authz-explain-close"
            style={{ background: "transparent", border: 0, color: "#A8A497", cursor: "pointer", fontSize: 14 }}>Close</button>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 8, marginBottom: 12 }}>
          <input
            value={resource} onChange={(e) => setResource(e.target.value)}
            placeholder="resource (e.g. projects)"
            data-testid="admin-shell-authz-explain-resource"
            style={{ background: "#242426", border: "1px solid #3A3A3D", borderRadius: 6, padding: "8px 10px", color: "#FAFAF7", fontFamily: "var(--font-mono)", fontSize: 12 }}
          />
          <input
            value={action} onChange={(e) => setAction(e.target.value)}
            placeholder="action (e.g. edit)"
            data-testid="admin-shell-authz-explain-action"
            style={{ background: "#242426", border: "1px solid #3A3A3D", borderRadius: 6, padding: "8px 10px", color: "#FAFAF7", fontFamily: "var(--font-mono)", fontSize: 12 }}
          />
          <button type="button" onClick={run} disabled={busy || !resource || !action}
            data-testid="admin-shell-authz-explain-run"
            style={{ background: "#B8892B", color: "#0F0F10", border: 0, borderRadius: 6, padding: "8px 14px", fontFamily: "inherit", fontSize: 12, fontWeight: 600, cursor: busy ? "wait" : "pointer" }}>
            {busy ? "…" : "Explain"}
          </button>
        </div>
        {err && (
          <div style={{ color: "#FF7A75", fontSize: 12, marginBottom: 8 }} data-testid="admin-shell-authz-explain-error">
            {err}
          </div>
        )}
        {result && (
          <div data-testid="admin-shell-authz-explain-result" style={{ fontSize: 12, lineHeight: 1.6 }}>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase", color: result.granted ? "#4FE0A2" : "#FF7A75", marginBottom: 6 }}>
              {result.granted ? "Granted" : "Denied"} · {result.reason}
            </div>
            <div style={{ color: "#A8A497" }}>
              <div>Role: <span style={{ fontFamily: "var(--font-mono)", color: "#FAFAF7" }}>{result.role_key}</span></div>
              {result.matched_permission && (
                <div>Matched permission: <span style={{ fontFamily: "var(--font-mono)", color: "#FAFAF7" }}>{result.matched_permission}</span></div>
              )}
              <div style={{ marginTop: 6 }}>Role permissions ({result.role_permissions_summary?.length ?? 0}):</div>
              <div style={{ maxHeight: 160, overflowY: "auto", background: "#0F0F10", border: "1px solid #2A2A2D", borderRadius: 6, padding: 8, marginTop: 4 }}>
                {(result.role_permissions_summary || []).map((p) => (
                  <div key={p} style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "#A8A497" }}>{p}</div>
                ))}
                {(!result.role_permissions_summary || result.role_permissions_summary.length === 0) && (
                  <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "#A8A497" }}>—</div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
